import { useMemo } from "react"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import GeneratedDoc from "models/generated-doc"
import { GEN_DOC_STATUS } from "models/gen-doc-status"
import { SCREENS } from "models/screen"
import { PermissionsSliceState } from "slices/auth/permissions/reducer"
import { ProfileSliceState } from "slices/auth/profile/reducer"

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({
        screens
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({
        employee
    })
)

export interface GeneratedDocPermissions {
    isHRAdmin: boolean
    submittable: boolean
    draftable: boolean
    downloadable: boolean
    editable: boolean
    approvable: boolean
    rejectable: boolean
    candidateRejectable: boolean
    approveOnBehalf: boolean
    approvalBlockedReason: string | null
}

const useGeneratedDocPermissions = (
    generatedDoc: GeneratedDoc | null,
    genDocStatus: GEN_DOC_STATUS | null
): GeneratedDocPermissions => {
    const { employee } = useSelector(selectProfile)
    const { screens } = useSelector(selectPermissions)

    const isHRAdmin = useMemo(
        () => screens.map((s: any) => s.code).some((c: string) => c === SCREENS.HR_ADMIN),
        [screens]
    )

    const isSubmitter = useMemo(
        () => generatedDoc?.submittedById === employee?.id,
        [generatedDoc, employee]
    )

    const submittable = useMemo(() => {
        // A document that has never been saved can always be submitted.
        if (!generatedDoc)
            return true;

        if (!genDocStatus)
            return false;

        // Only a draft or a rejected document is waiting to be sent for approval.
        return (genDocStatus === GEN_DOC_STATUS.DRAFT || genDocStatus === GEN_DOC_STATUS.REJECT) && (isSubmitter || isHRAdmin)
    }, [genDocStatus, generatedDoc, isSubmitter, isHRAdmin])

    const draftable = useMemo(() => {
        // Nothing saved yet, so saving a draft is the only thing to do.
        if (!generatedDoc)
            return true;

        if (!genDocStatus)
            return false;

        // Once approved the document is frozen; anything earlier can be pulled back to draft.
        return genDocStatus !== GEN_DOC_STATUS.APPROVE && (isSubmitter || isHRAdmin)
    }, [genDocStatus, generatedDoc, isSubmitter, isHRAdmin])

    const downloadable = useMemo(() => {
        if (!genDocStatus || !generatedDoc)
            return false;

        // Only a fully approved document is worth handing out.
        return genDocStatus === GEN_DOC_STATUS.APPROVE && (isSubmitter || isHRAdmin)
    }, [genDocStatus, generatedDoc, isSubmitter, isHRAdmin])

    const editable = useMemo(() => {
        // A brand new document starts out editable.
        if (!generatedDoc)
            return true;

        if (!genDocStatus)
            return false;

        // Fields unlock in draft only. A rejected document has to be pulled back to draft first.
        return genDocStatus === GEN_DOC_STATUS.DRAFT && (isSubmitter || isHRAdmin)
    }, [genDocStatus, generatedDoc, isSubmitter, isHRAdmin])

    const approvable = useMemo(() => {
        // Approvals only happen while the document is out for approval.
        if (!generatedDoc || genDocStatus !== GEN_DOC_STATUS.PENDING)
            return false;

        // HR reviews first. Any HR admin can clear it, so this is a screen check
        // rather than a match against a named approver.
        if (isHRAdmin && !generatedDoc.hrApprovedAt)
            return true;

        // Until HR has cleared it nobody further down the chain can act.
        if (!generatedDoc.hrApprovedAt)
            return false;

        // If you are the hiring manager
        if (generatedDoc.managerApproverId === employee?.id)
            return !generatedDoc.managerApprovedAt;

        // If you are the manager's manager, the hiring manager has to go before you
        if (generatedDoc.higherManagerApproverId === employee?.id)
            return !generatedDoc.higherManagerApprovedAt && !!generatedDoc.managerApprovedAt;

        // If you are the president
        if (generatedDoc.presidentApproverId === employee?.id)
            return !generatedDoc.higherManagerApprovedAt && !!generatedDoc.managerApprovedAt;

        return false
    }, [genDocStatus, generatedDoc, employee, isHRAdmin])

    const approveOnBehalf = useMemo(() => {
        if (!genDocStatus || !generatedDoc || !employee) return false;

        // HR clears its own stage before it can record approvals for anyone else.
        return genDocStatus === GEN_DOC_STATUS.PENDING
            && isHRAdmin
            && !!generatedDoc.hrApprovedAt
            && !generatedDoc.presidentApprovedAt;
    }, [genDocStatus, generatedDoc, employee, isHRAdmin])

    const rejectable = useMemo(() => {
        if (!genDocStatus || !generatedDoc)
            return false;

        // HR reviews first, so any HR admin can reject until HR's stage is cleared.
        if (isHRAdmin && !generatedDoc.hrApprovedAt)
            return genDocStatus === GEN_DOC_STATUS.PENDING;

        // if CLU have already approved it, you cannot reject it
        if (generatedDoc.managerApproverId === employee?.id && generatedDoc.managerApprovedAt) {
            return false
        }

        // if CLU have already approved it, you cannot reject it
        if (generatedDoc.higherManagerApproverId === employee?.id && generatedDoc.higherManagerApprovedAt) {
            return false
        }

        // if CLU have already approved it, you cannot reject it
        if (generatedDoc.presidentApproverId === employee?.id && generatedDoc.higherManagerApprovedAt) {
            return false
        }

        // Only a named approver on the document can reject it.
        return genDocStatus === GEN_DOC_STATUS.PENDING && (generatedDoc.managerApproverId === employee?.id || generatedDoc.higherManagerApproverId === employee?.id || generatedDoc.presidentApproverId === employee?.id)
    }, [genDocStatus, generatedDoc, employee, isHRAdmin])

    const candidateRejectable = useMemo(() => {
        return false

        // if (!genDocStatus || !generatedDoc)
            // return false;

        // return genDocStatus === GEN_DOC_STATUS.APPROVE && (isSubmitter || isHRAdmin);
    }, [genDocStatus, generatedDoc, isSubmitter, isHRAdmin])

    // Why the Approve button is disabled for someone who is on the document but cannot act yet.
    const approvalBlockedReason = useMemo(() => {
        if (approvable || !generatedDoc || genDocStatus !== GEN_DOC_STATUS.PENDING)
            return null;

        if (!generatedDoc.hrApprovedAt)
            return "HR still needs to approve.";

        if (generatedDoc.higherManagerApproverId === employee?.id && !generatedDoc.managerApprovedAt)
            return "Hiring manager still needs to approve.";

        if (generatedDoc.presidentApproverId === employee?.id && !generatedDoc.higherManagerApprovedAt)
            return "The manager's manager still needs to approve.";

        return "You have already approved this document.";
    }, [approvable, genDocStatus, generatedDoc, employee])

    return {
        isHRAdmin,
        submittable,
        draftable,
        downloadable,
        editable,
        approvable,
        rejectable,
        candidateRejectable,
        approveOnBehalf,
        approvalBlockedReason
    }
}

export default useGeneratedDocPermissions
